import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { AdminProvider } from './context/AdminContext';
import { RegionProvider } from './context/RegionContext';
import { ClientAuthProvider } from './context/ClientAuthContext';
import { StaffAuthProvider } from './context/StaffAuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import ScrollToTop from './components/ScrollToTop';
import RegionSelector from './pages/RegionSelector';
import HomePageIreland from './pages/HomePageIreland';
import ServicesPage from './pages/ServicesPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import BookingPage from './pages/BookingPage';
import GalleryPage from './pages/GalleryPage';
import QuotePage from './pages/QuotePage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import CookiesPage from './pages/CookiesPage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import ClientAuth from './pages/ClientAuth';
import ClientDashboard from './pages/ClientDashboard';
import StaffLogin from './pages/StaffLogin';
import StaffDashboard from './pages/StaffDashboard';
import StaffJobDetails from './pages/StaffJobDetails';
import StaffProfile from './pages/StaffProfile';
import StaffWorkHours from './pages/StaffWorkHours';
import './App.css';

const Layout = ({ children, hideHeaderFooter = false }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isStaffRoute = location.pathname.startsWith('/staff');
  const isClientAuthRoute = location.pathname.includes('/client/login');
  const isRegionSelector = location.pathname === '/';

  // Don't show header/footer on region selector, client auth, or staff routes
  if (isRegionSelector || hideHeaderFooter || isStaffRoute) {
    return <main>{children}</main>;
  }

  return (
    <RegionProvider>
      {!isAdminRoute && !isClientAuthRoute && <Header />}
      <main>{children}</main>
      {!isAdminRoute && !isClientAuthRoute && <Footer />}
      {!isAdminRoute && !isClientAuthRoute && <WhatsAppButton />}
    </RegionProvider>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AdminProvider>
        <ClientAuthProvider>
          <StaffAuthProvider>
            <BrowserRouter>
              <ScrollToTop />
              <div className="App">
                <Routes>
                  {/* Region Selector - Landing Page (redirects to /ie) */}
                  <Route path="/" element={<RegionSelector />} />
                  
                  {/* Portugal Routes - REDIRECT to Ireland */}
                  <Route path="/pt" element={<Navigate to="/ie" replace />} />
                  <Route path="/pt/*" element={<Navigate to="/ie" replace />} />
                  
                  {/* Ireland Routes - Main Site */}
                  <Route path="/ie" element={<Layout><HomePageIreland /></Layout>} />
                  <Route path="/ie/services" element={<Layout><ServicesPage /></Layout>} />
                  <Route path="/ie/about" element={<Layout><AboutPage /></Layout>} />
                  <Route path="/ie/contact" element={<Layout><ContactPage /></Layout>} />
                  <Route path="/ie/booking" element={<Layout><BookingPage /></Layout>} />
                  <Route path="/ie/quote" element={<QuotePage />} />
                  <Route path="/ie/gallery" element={<Layout><GalleryPage /></Layout>} />
                  <Route path="/ie/privacy" element={<Layout><PrivacyPage /></Layout>} />
                  <Route path="/ie/terms" element={<Layout><TermsPage /></Layout>} />
                  <Route path="/ie/cookies" element={<Layout><CookiesPage /></Layout>} />
                  <Route path="/ie/client/login" element={<Layout hideHeaderFooter><ClientAuth /></Layout>} />
                  <Route path="/ie/client/dashboard" element={<Layout><ClientDashboard /></Layout>} />
                  
                  {/* Admin Routes (shared) */}
                  <Route path="/admin/login" element={<Layout><AdminLogin /></Layout>} />
                  <Route path="/admin/dashboard" element={<Layout><AdminDashboard /></Layout>} />
                  
                  {/* Staff Portal Routes */}
                  <Route path="/staff/login" element={<StaffLogin />} />
                  <Route path="/staff/dashboard" element={<StaffDashboard />} />
                  <Route path="/staff/job/:jobId" element={<StaffJobDetails />} />
                  <Route path="/staff/profile" element={<StaffProfile />} />
                  <Route path="/staff/hours" element={<StaffWorkHours />} />
                  
                  {/* Legacy routes - Redirect to /ie */}
                  <Route path="/services" element={<Navigate to="/ie/services" replace />} />
                  <Route path="/servicos" element={<Navigate to="/ie/services" replace />} />
                  <Route path="/about" element={<Navigate to="/ie/about" replace />} />
                  <Route path="/contact" element={<Navigate to="/ie/contact" replace />} />
                  <Route path="/booking" element={<Navigate to="/ie/booking" replace />} />
                  <Route path="/gallery" element={<Navigate to="/ie/gallery" replace />} />
                  
                  {/* Catch-all redirect to /ie */}
                  <Route path="*" element={<Navigate to="/ie" replace />} />
                </Routes>
              </div>
            </BrowserRouter>
          </StaffAuthProvider>
        </ClientAuthProvider>
      </AdminProvider>
    </LanguageProvider>
  );
}

export default App;
